import { cn } from '@/shared/lib/cn';
import { type FC, type SVGProps } from 'react';
import { AlertIcon } from './alert';
import { BellIcon } from './bell';
import { ChartIcon } from './chart';
import { ChevronLeftIcon } from './chevron-left';
import { ChevronRightIcon } from './chevron-right';
import { CloseXIcon } from './close-x';
import { ErrorIcon } from './error-circle';
import { ExitIcon } from './exit';
import { EyeIcon } from './eye';
import { EyeInvisibleIcon } from './eye-invisible';
import { GearIcon } from './gear';
import { HomeIcon } from './home';
import { InfoCircleIcon } from './info-circle';
import { ProfileCardIcon } from './profile-card';
import { RequiredIcon } from './required-icon';
import { SearchIcon } from './search';
import { SortAscIcon } from './sort-asc';
import { SortDescIcon } from './sort-desc';
import { SortInactiveIcon } from './sort-inactive';
import { SuccessIcon } from './success-circle';
import { TwoAvatarsIcon } from './two-avatars';
import { UserIcon } from './user';
import { WarningIcon } from './warning-circle';

import { AIStarsIcon } from './ai-stars';
import { AISummaryIcon } from './ai-summary';
import { ArrowDownIcon } from './arrow-down';
import { ArrowRightIcon } from './arrow-right';
import { ArrowUpIcon } from './arrow-up';
import { AttachmentIcon } from './attachment-icon';
import { BedIcon } from './bed';
import { BorderedPlusIcon } from './bordered-plus';
import { CalendarIcon } from './calendar';
import { ChartLineIcon } from './chart-line-icon';
import { CheckIcon } from './check';
import { ChevronDownIcon } from './chevron-down';
import { CopyIcon } from './copy-icon';
import { DollarIcon } from './dollar';
import { DoubleChevronRightIcon } from './double-right';
import { DownloadIcon } from './download-icon';
import { EditPencilIcon } from './edit-pencil';
import { EnvironmentIcon } from './environment';
import { ExclamationCircleIcon } from './exclamation-circle';
import { FileAddIcon } from './file-add';
import { FileTextIcon } from './file-text';
import { FlagIcon } from './flag';
import { IdCardIcon } from './id-card';
import { InboxDropZoneIcon } from './inbox-dropzone';
import { infoCircleTransparent } from './info-icon-transparent';
import { InsightRecommendedIcon } from './insight-recommended';
import { InsightWarningIcon } from './insight-warning';
import { InviteUserIcon } from './invite-user';
import { LikeIcon } from './like';
import { LoadingIcon } from './loading';
import { LocationIcon } from './location';
import { LocationFilledIcon } from './location-filled';
import { MarginIcon } from './margin';
import { MoonIcon } from './moon';
import { PhoneIcon } from './phone';
import { PlusIcon } from './plus';
import { PlusCircleIcon } from './plus-circle';
import { RefreshIcon } from './refresh';
import { ScheduleIcon } from './schedule';
import { HappyIcon } from './smile-happy';
import { NeutralIcon } from './smile-neutral';
import { SadIcon } from './smile-sad';
import { SolutionIcon } from './solution';
import { StarIcon } from './star';
import { SunIcon } from './sun';
import { TeamIcon } from './team';
import { TrashIcon } from './trash';
import { WarningTriangleIcon } from './warning-triangle';
import { XMarkCircleIcon } from './x-mark-circle';
import { XlsxIcon } from './xlsx';

const icons = {
  bell: BellIcon,
  user: UserIcon,
  gear: GearIcon,
  chart: ChartIcon,
  home: HomeIcon,
  eye: EyeIcon,
  exit: ExitIcon,
  star: StarIcon,
  required: RequiredIcon,
  search: SearchIcon,
  alert: AlertIcon,
  copy: CopyIcon,
  trash: TrashIcon,
  check: CheckIcon,
  refresh: RefreshIcon,
  loading: LoadingIcon,
  dollar: DollarIcon,
  team: TeamIcon,
  flag: FlagIcon,
  phone: PhoneIcon,
  bed: BedIcon,
  solution: SolutionIcon,
  environment: EnvironmentIcon,
  calendar: CalendarIcon,
  schedule: ScheduleIcon,
  plus: PlusIcon,
  location: LocationIcon,
  xlsx: XlsxIcon,
  margin: MarginIcon,
  like: LikeIcon,
  'location-filled': LocationFilledIcon,
  'info-circle-transparent': infoCircleTransparent,
  'id-card': IdCardIcon,
  'bordered-plus': BorderedPlusIcon,
  'exclamation-circle': ExclamationCircleIcon,
  'edit-pencil': EditPencilIcon,
  'invite-user': InviteUserIcon,
  'close-x': CloseXIcon,
  'sort-asc': SortAscIcon,
  'chevron-down': ChevronDownIcon,
  'sort-desc': SortDescIcon,
  'sort-inactive': SortInactiveIcon,
  'eye-invisible': EyeInvisibleIcon,
  'chevron-left': ChevronLeftIcon,
  'chevron-right': ChevronRightIcon,
  'two-avatars': TwoAvatarsIcon,
  'profile-card': ProfileCardIcon,
  'info-circle': InfoCircleIcon,
  'error-circle': ErrorIcon,
  'warning-circle': WarningIcon,
  'success-circle': SuccessIcon,
  'arrow-up': ArrowUpIcon,
  'arrow-down': ArrowDownIcon,
  'arrow-right': ArrowRightIcon,
  'warning-triangle': WarningTriangleIcon,
  'chart-line': ChartLineIcon,
  'ai-stars': AIStarsIcon,
  'ai-summary': AISummaryIcon,
  'insight-warning': InsightWarningIcon,
  'insight-recommended': InsightRecommendedIcon,
  'smile-happy': HappyIcon,
  'smile-neutral': NeutralIcon,
  'smile-sad': SadIcon,
  'double-right': DoubleChevronRightIcon,
  'file-add': FileAddIcon,
  'inbox-dropzone': InboxDropZoneIcon,
  'icon-attachment': AttachmentIcon,
  'download-icon': DownloadIcon,
  'file-text': FileTextIcon,
  'plus-circle': PlusCircleIcon,
  'x-mark-circle': XMarkCircleIcon,
  sun: SunIcon,
  moon: MoonIcon,
};

export type IconName = keyof typeof icons;

type IconProps = {
  size?: number;
  name: IconName;
} & SVGProps<SVGSVGElement>;

export const Icon: FC<IconProps> = ({
  size = 16,
  name,
  className,
  ...props
}) => {
  const Component = icons[name];

  return (
    <Component
      width={size}
      height={size}
      className={cn('shrink-0', className)}
      {...props}
    />
  );
};
